import { gql } from '@apollo/client';
import { get, toNumber, isEmpty } from 'lodash-es';
import { matchPath } from 'react-router-dom';
import { logger } from 'client/utils/logger/logger';
import { EdmundsAPI } from 'client/data/api/api-client';
import { withMetrics } from 'client/data/api/api-metrics';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { EdmundsGraphQLFederation } from 'client/data/graphql/graphql-client';
import { VisitorModel } from 'client/data/models/visitor';
import { buildStyleIdsShotTypePhotosPathsWithPageSize, PhotosModel } from 'client/data/models/photos';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import {
  GEM_DEAL_THRESHOLD,
  DEALS_FILTER_VALUES,
  FILTER_QUERY_PARAMS,
  SORT_BY_CONFIG,
  PAGE_SIZE,
} from 'site-modules/shared/constants/lease-deals/lease-deals';
import { PageModel } from 'client/data/models/page';
import { getQuery } from 'client/utils/location';
import {
  parseFiltersFromUrl,
  mergeParamsWithQuery,
  mapParamSlugsToValues,
} from 'site-modules/shared/utils/lease-deals/filter-url-utils';
import { processFilters } from 'client/site-modules/shared/utils/lease-deals/process-filters';
import {
  createLeaseDealsFacetsVariables,
  createLeaseDealsVariables,
} from 'site-modules/shared/utils/lease-deals/filter-builder';
import { getSortParamWithFallback } from 'client/site-modules/shared/utils/lease-deals/lease-deals-filter-utils';
import { LEASE_DEALS_PATHS } from 'site-modules/shared/constants/lease-deals/routes';
import { buildPageSlugToValueMap } from 'site-modules/lease-deals/utils/lease-deals-slug-value-map-builder';
import { UrlModel } from 'client/data/models/url';
import { makeNiceName } from 'client/site-modules/shared/utils/nice-name';
import { DESKTOP_IMAGE_WIDTH } from 'client/site-modules/lease-deals/constants/lease-deals-constants';

const getLeaseDealsPath = () => 'leaseDealsData';
const getPhotosPath = width => `photos.width["${width}"]`;
const getSelectedFiltersPath = () => 'selectedFilters';
const getFiltersPath = () => 'filters';
const getRouteFiltersPath = () => 'routeFilters';
const getNearbyMakeInventoryPath = () => 'nearbyMakeInventory';
const getSelectedDealPath = id => `selectedDeal.id["${id}"]`;

export const LeaseDealsPaths = {
  getLeaseDealsPath,
  getPhotosPath,
  getSelectedFiltersPath,
  getFiltersPath,
  getRouteFiltersPath,
  getNearbyMakeInventoryPath,
  getSelectedDealPath,
};

const modifyLeaseDealsResponse = (leaseDealsResponse, makeCounts) => {
  const deals = leaseDealsResponse.deals.map(topDealObj => {
    const {
      id,
      activeListingStats,
      endDate,
      term,
      msrp,
      dueAtSigning,
      disclaimer,
      payment,
      paymentRatio,
      effectivePayment,
      effectivePaymentRatio,
      lifetimeCost,
      lifetimeCostRatio,
      url,
      yearlyMileage,
      inventoryBodyType,
      modelYear,
      style,
      publishDate,
    } = topDealObj.deal;
    const { makeName, modelName, makeSlug, modelSlug, year, segmentRatings } = modelYear;
    const { numberOfSeats, id: styleId, trimName, trimSlug, engine, driveTrain, epaSpecifications } = style;
    const edmundsRating =
      segmentRatings?.find(({ baseStyleId }) => baseStyleId === styleId)?.rating || segmentRatings?.[0]?.rating;
    return {
      id,
      endDate,
      term,
      msrp,
      dueAtSigning,
      disclaimer,
      payment,
      paymentRatio,
      effectivePayment,
      effectivePaymentRatio,
      lifetimeCost,
      lifetimeCostRatio,
      bodyType: inventoryBodyType,
      driveType: driveTrain,
      engineType: engine?.engineType,
      make: makeName,
      makeNiceName: makeSlug,
      mileageYearlyExcess: yearlyMileage,
      model: modelName,
      modelNiceName: modelSlug,
      seatingCapacity: numberOfSeats,
      styleId: toNumber(styleId),
      trim: trimName,
      trimNiceName: trimSlug,
      webUrls: url,
      year,
      showCta: makeCounts?.[makeName] > 0,
      paymentMedian: activeListingStats?.paymentMedian,
      effectivePaymentMedian: activeListingStats?.effectivePaymentMedian,
      isGemDeal: effectivePaymentRatio >= GEM_DEAL_THRESHOLD,
      publishDate,
      electricityRange: epaSpecifications?.electricityRange,
      combinedMpg: epaSpecifications?.combinedMpg,
      edmundsRating,
    };
  });

  return { ...leaseDealsResponse, deals };
};

const getMakeCounts = async (context, deals, zipCode) => {
  const makesList = [...new Set(deals.map(({ deal }) => deal.modelYear.makeName))].join(',');
  const inventoryUrl = `/inventory/v5/aggregate?by=vehicleInfo.styleInfo.make&type=NEW&top=100&zip=${zipCode}&radius=100&vehicleInfo.styleInfo.make=${makesList}&deliveryType=local`;

  return withMetrics(EdmundsAPI, context).fetchJson(inventoryUrl);
};

const getLeaseDeals = async (context, zipCode, selectedFilters) => {
  const variables = createLeaseDealsVariables({ ...selectedFilters, zipCode });
  const graphqlResponse = await withMetrics(EdmundsGraphQLFederation, context).query(
    gql`
      query ($leaseDealFilters: LeaseDealFilters!, $page: PageRequest!, $sorts: [LeaseDealSort!]) {
        leaseDeals(leaseDealFilters: $leaseDealFilters) {
          aggregation {
            topDealsByModelCount
            topDealsByModel(page: $page, sorts: $sorts) {
              deal {
                id
                modelYear {
                  modelName
                  makeName
                  makeSlug
                  modelSlug
                  year
                  segmentRatings {
                    rating
                    baseStyleId
                  }
                }
                style {
                  id
                  trimName
                  trimSlug
                  driveTrain
                  numberOfSeats
                  engine {
                    engineType
                  }
                  epaSpecifications {
                    electricityRange
                    combinedMpg
                  }
                }
                endDate
                url
                term
                msrp
                inventoryBodyType
                dueAtSigning
                disclaimer
                yearlyMileage
                payment
                paymentRatio
                effectivePayment
                effectivePaymentRatio
                lifetimeCost
                lifetimeCostRatio
                publishDate
                activeListingStats {
                  paymentMedian
                  paymentMin
                  paymentMax
                  lifetimeCostMedian
                  effectivePaymentMedian
                  effectivePaymentMin
                  effectivePaymentMax
                }
              }
            }
          }
        }
      }
    `,
    variables
  );

  return {
    topDealsByModelCount: graphqlResponse?.leaseDeals?.aggregation?.topDealsByModelCount ?? 0,
    deals: graphqlResponse?.leaseDeals?.aggregation?.topDealsByModel ?? [],
  };
};

const DEFAULT_FILTERS = {
  deals: [DEALS_FILTER_VALUES.GEMS, DEALS_FILTER_VALUES.ALL],
  sortBy: Object.keys(SORT_BY_CONFIG),
};

const queryLeaseDealsFacets = async (context, selectedFilters) => {
  const variables = createLeaseDealsFacetsVariables(selectedFilters);

  return withMetrics(EdmundsGraphQLFederation, context).query(
    gql`
      query Facets($leaseDealFilters: LeaseDealFilters!) {
        leaseDeals(leaseDealFilters: $leaseDealFilters) {
          aggregation {
            facets {
              make: makeFacets {
                value
                count
              }
              engineType: engineTypeFacets {
                value
                count
              }
              bodyType: bodyTypeFacets {
                value
                count
              }
              monthlyPayment: paymentFacets {
                min
                max
              }
              dueAtSigning: dueAtSigningFacets {
                min
                max
              }
              term: termFacets {
                value
                count
              }
              mileageAllowance: yearlyMileageFacets {
                value
                count
              }
              totalSeating: numberOfSeatsFacets {
                value
                count
              }
            }
          }
        }
      }
    `,
    variables
  );
};

export const LeaseDealsModel = createModelSegment('leaseDeals', [
  /**
   @see getFiltersPath
   */
  {
    path: 'filters',
    async resolve(_, context) {
      const [{ zipCode }, selectedFilters] = await Promise.all([
        context.resolveValue('location', VisitorModel),
        context.resolveValue(getSelectedFiltersPath(), LeaseDealsModel, false),
      ]);

      try {
        const graphqlResponse = await queryLeaseDealsFacets(context, { ...selectedFilters, zipCode });
        const filterFacets = graphqlResponse.leaseDeals.aggregation.facets;
        return processFilters({ selectedFilters, filterFacets, defaultFilters: DEFAULT_FILTERS });
      } catch (e) {
        logger.error('Failed to load data', e);
        return DEFAULT_FILTERS;
      }
    },
    async update(_, match, context) {
      const { zipCode } = await context.resolveValue('location', VisitorModel);
      const selectedFilters = context.getValue(getSelectedFiltersPath(), LeaseDealsModel);

      try {
        const graphqlResponse = await queryLeaseDealsFacets(context, { ...selectedFilters, zipCode });
        const filterFacets = graphqlResponse.leaseDeals.aggregation.facets;
        return processFilters({ selectedFilters, filterFacets, defaultFilters: DEFAULT_FILTERS });
      } catch (e) {
        logger.error('Failed to load data', e);
        return DEFAULT_FILTERS;
      }
    },
  },
  {
    path: 'routeFilters',
    async resolve(match, context) {
      const [{ pathname }, urlContext] = await Promise.all([
        context.resolveValue('location', PageModel),
        context.resolveValue('context', UrlModel),
      ]);

      const matchedRoute = LEASE_DEALS_PATHS.reduce(
        (found, routePath) => found || matchPath(pathname, { path: routePath, exact: true }),
        null
      );

      return {
        path: matchedRoute?.path,
        ...mapParamSlugsToValues(
          matchedRoute?.params,
          buildPageSlugToValueMap({ routePath: matchedRoute?.path, urlContext, params: matchedRoute?.params })
        ),
      };
    },
  },
  /**
   @see getSelectedFiltersPath
   */
  {
    path: 'selectedFilters',
    async resolve(match, context) {
      // be careful because `routeFilters` will be returned as `null` when resolved, not `undefined`
      const [routeFilters, location] = await Promise.all([
        context.resolveValue(LeaseDealsPaths.getRouteFiltersPath(), LeaseDealsModel, false),
        context.resolveValue('location', PageModel),
      ]);
      const query = getQuery(location);
      const parsedFilters = parseFiltersFromUrl(mergeParamsWithQuery(routeFilters, query));

      return {
        ...parsedFilters,
        [FILTER_QUERY_PARAMS.SORT_BY]: [getSortParamWithFallback(parsedFilters[FILTER_QUERY_PARAMS.SORT_BY]?.[0])],
      };
    },
    async update(newValue, match, context) {
      const selectedFilters = await context.resolveValue('selectedFilters');

      const buildFiltersObject = filters => ({
        ...filters,
        [FILTER_QUERY_PARAMS.SORT_BY]: [getSortParamWithFallback(filters[FILTER_QUERY_PARAMS.SORT_BY]?.[0])],
      });

      if (isEmpty(newValue)) return buildFiltersObject({});

      const { shouldOverride, ...rest } = newValue;
      const combinedValues = { ...selectedFilters, ...newValue };

      return buildFiltersObject(shouldOverride ? rest : combinedValues);
    },
  },
  {
    /**
      @see getLeaseDealsPath
    */
    path: 'leaseDealsData',
    async resolve(_, context) {
      const [{ zipCode }, selectedFilters] = await Promise.all([
        context.resolveValue('location', VisitorModel),
        context.resolveValue(getSelectedFiltersPath()),
      ]);

      const { id: _id, ...filtersWithoutId } = selectedFilters;

      try {
        const leaseDealsResponse = await getLeaseDeals(context, zipCode, {
          ...filtersWithoutId,
          pageSize: PAGE_SIZE,
        });
        if (!leaseDealsResponse.deals.length) {
          return { deals: [], topDealsByModelCount: 0 };
        }

        const makeCountResponse = await getMakeCounts(context, leaseDealsResponse.deals, zipCode);

        return modifyLeaseDealsResponse(leaseDealsResponse, makeCountResponse['vehicleInfo.styleInfo.make']);
      } catch (error) {
        logger.error('lease-deals.js - leaseDealsData path - failure', error);
        return { deals: [], topDealsByModelCount: 0 };
      }
    },
  },
  {
    /**
     * @see getSelectedDealPath
     */
    path: 'selectedDeal.id["{id}"]',
    async resolve(match, context) {
      const { zipCode } = await context.resolveValue('location', VisitorModel, false);

      const dealId = match.id;
      if (!dealId) return context.abort();

      try {
        const response = await getLeaseDeals(context, zipCode, {
          id: [dealId],
          [FILTER_QUERY_PARAMS.SORT_BY]: [getSortParamWithFallback()],
        });

        if (!response.deals.length) return null;

        const selectedDeal = response.deals.find(({ deal }) => String(deal.id) === String(dealId));

        if (!selectedDeal) return null;

        const styleId = selectedDeal.deal?.style?.id;

        const [makeCountResult, photosResult] = (
          await Promise.allSettled([
            getMakeCounts(context, [selectedDeal], zipCode),
            styleId
              ? context.resolveValue(
                  buildStyleIdsShotTypePhotosPathsWithPageSize(styleId, 'OEM', 'FQ', DESKTOP_IMAGE_WIDTH, 1),
                  PhotosModel
                )
              : Promise.resolve(undefined),
          ])
        ).map(responseItem => (responseItem.status === 'fulfilled' ? responseItem.value : undefined));

        const modifiedDeals = modifyLeaseDealsResponse(
          { ...response, deals: [selectedDeal] },
          makeCountResult?.['vehicleInfo.styleInfo.make']
        );
        const photo = get(photosResult, 'photos', []).find(({ styleIds }) => styleIds?.includes(styleId));

        return {
          ...modifiedDeals.deals[0],
          photo: { href: get(photo, 'sources[0].link.href', null), title: get(photo, 'title', null) },
        };
      } catch (e) {
        logger.error('lease-deals.js - selectedDeal path - failure', e);
        return null;
      }
    },
  },
  {
    /**
     * @see getNearbyMakeInventoryPath
     */
    path: 'nearbyMakeInventory',
    async resolve(match, context) {
      const [{ latitude, longitude, zipCode, dma, stateCode }, routeFilters] = await Promise.all([
        context.resolveValue('location', VisitorModel),
        context.resolveValue(LeaseDealsPaths.getRouteFiltersPath(), LeaseDealsModel),
      ]);

      if (!routeFilters?.make) {
        return context.abort();
      }

      const queryParams = objectToQueryString({
        '-dealerInfo.dealerRelation': 'RESTRICTED',
        '-dealerInfo.displayInfo.parentDealershipName': 'Edmunds',
        deliveryType: 'all',
        facets: false,
        lat: latitude,
        lon: longitude,
        pagenum: 1,
        pagesize: 21,
        radius: 25.0,
        searchable: true,
        sortby: 'boostControl5LeaseDeals:DESC',
        type: 'NEW',
        userDma: dma,
        userState: stateCode,
        'vehicleInfo.styleInfo.make.nicename': makeNiceName(routeFilters.make),
        zip: zipCode,
      });

      const data = await withMetrics(EdmundsAPI, context).fetchJson(`/inventory/v5/searchresultspage?${queryParams}`);
      const items = data?.inventories?.results ?? [];
      const sorted = [...items].sort(
        (a, b) =>
          (a.prices?.estimateLeasePromise?.monthlyPayment ?? Infinity) -
          (b.prices?.estimateLeasePromise?.monthlyPayment ?? Infinity)
      );

      return {
        inventoryCount: data?.inventories?.totalNumber ?? 0,
        lowestLease: sorted[0]?.prices?.estimateLeasePromise?.monthlyPayment ?? null,
        highestLease: sorted[sorted.length - 1]?.prices?.estimateLeasePromise?.monthlyPayment ?? null,
      };
    },
  },
  {
    /**
      @see getPhotosPath
    */
    path: 'photos.width["{width}"]',
    async resolve(match, context) {
      const leaseDealsData = await context.resolveValue(getLeaseDealsPath());

      const styleIds = leaseDealsData.deals?.map(({ styleId }) => styleId).join(',');
      if (!styleIds) return [];

      try {
        const response = await context.resolveValue(
          buildStyleIdsShotTypePhotosPathsWithPageSize(styleIds, 'OEM', 'FQ', match.width, 40),
          PhotosModel
        );

        return get(response, 'photos', []).map(photo => ({
          href: get(photo, 'sources[0].link.href', null),
          title: get(photo, 'title', null),
          styleIds: get(photo, 'styleIds', []).reduce(
            (styleIdsObj, styleId) => ({ ...styleIdsObj, [+styleId]: true }),
            {}
          ),
        }));
      } catch (e) {
        logger.error('lease-deals.js - getPhotosPath path - failure', e);
        return [];
      }
    },
  },
]);
